import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { useTheme } from "@mui/material";
import { Circle } from "@mui/icons-material";
import { ProgrammePerformanceData, ProgrammeTableData } from "../types";

interface InvestmentPerformanceChartProps {
  chartData: ProgrammePerformanceData[];
  tableData: ProgrammeTableData[];
}

const getRagColor = (
  rag: "green" | "amber" | "red"
): "success" | "warning" | "error" => {
  switch (rag) {
    case "green":
      return "success";
    case "amber":
      return "warning";
    case "red":
      return "error";
  }
};

export const InvestmentPerformanceChart = ({
  chartData,
  tableData,
}: InvestmentPerformanceChartProps) => {
  const theme = useTheme();

  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "action.hover",
        }}
      >
        <Typography variant="subtitle2">
          Investment Performance Overview
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Planned → Committed → Raised → Actual by Programme
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        <Box sx={{ height: 350, width: "100%", mb: 3 }}>
          <BarChart
            xAxis={[
              {
                scaleType: "band",
                data: chartData.map((item) => item.programme),
                tickLabelStyle: { fontSize: 12 },
                height: 30,
              },
            ]}
            yAxis={[
              {
                tickLabelStyle: { fontSize: 12 },
                valueFormatter: (value: number) => `${value}%`,
              },
            ]}
            series={[
              {
                data: chartData.map((item) => item.planned),
                label: "Planned",
                color: theme.palette.success.main,
              },
              {
                data: chartData.map((item) => item.committed),
                label: "Committed",
                color: theme.palette.success.light,
              },
              {
                data: chartData.map((item) => item.raised),
                label: "Raised",
                color: theme.palette.info.light,
              },
              {
                data: chartData.map((item) => item.actual),
                label: "Actual",
                color: theme.palette.secondary.dark,
              }
            ]}
            height={350}
            slotProps={{
              legend: {
                position: { vertical: "top", horizontal: "end" },
              },
            }}
            grid={{ vertical: true, horizontal: true }}
            borderRadius={4}
          />
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Programme</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Planned £
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Committed £
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Raised £
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Actual £
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Variance RAG
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">
                  RAG
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.map((row) => (
                <TableRow key={row.programme} hover>
                  <TableCell>{row.programme}</TableCell>
                  <TableCell align="right">{row.planned}</TableCell>
                  <TableCell align="right">{row.committed}</TableCell>
                  <TableCell align="right">{row.raised}</TableCell>
                  <TableCell align="right">{row.actual}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontSize="0.875rem">
                      {row.variance}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Circle
                      fontSize="small"
                      color={getRagColor(row.rag)}
                      sx={{ fontSize: 16 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

      </Box>
    </Card>
  );
};
